import { useState } from 'react'
import { Platform, ScrollView, useWindowDimensions } from 'react-native'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller'
import useRWD, { useMaxWindowDimensions } from '@ui/theme/useRWD'
import cn from '@ui/utils/cn'

import ModalCard from '../ModalCard'
import useModalContext from '../useModalContext'
import { ModalCardComponentProps, ModalCardProps } from '../types'

export const MODAL_BOTTOM_SCREEN_GAP = Platform.select({
  default: 32,
  web: 64,
})

export function ModalBottomCard({
  children,
  disableScroll,
  styleCard,
  justifyContent = Platform.select({ default: 'flex-end', web: 'center' }),
}: ModalCardComponentProps) {
  const dimensions = useWindowDimensions()
  const { screen } = useRWD()
  const [element, setElement] = useState({ width: 0, height: 0 })
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation()

  const { animation } = useModalContext()
  const { width: sWidth } = useMaxWindowDimensions()
  const { width: rWidth } = screen

  const width = sWidth - 2 * MODAL_BOTTOM_SCREEN_GAP

  const bottom =
    justifyContent === 'flex-end'
      ? MODAL_BOTTOM_SCREEN_GAP
      : screen.height / 2 - element.height / 2

  const left = dimensions.width / 2 - element.width / 2

  const styleModal = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY:
            interpolate(animation.value, [0, 100], [400, 0]) +
            keyboardHeight.value,
        },
      ],
      opacity: interpolate(animation.value, [0, 100], [0, 1]),
      pointerEvents: animation.value < 50 ? 'none' : 'auto',
      maxHeight:
        screen.height - 2 * MODAL_BOTTOM_SCREEN_GAP - keyboardHeight.value * -1,
    }
  })

  return (
    <Animated.View
      className={cn('absolute overflow-hidden rounded-2xl bg-white')}
      style={[
        {
          width,
          left,
          bottom,
          flex: 1,
        },
        styleModal,
        styleCard,
      ]}
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout
        setElement({ width, height })
      }}
    >
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="relative inset-0"
        scrollEnabled={!disableScroll}
      >
        {children}
      </ScrollView>
    </Animated.View>
  )
}

export default function ModalBottom(props: ModalCardProps) {
  return (
    <ModalCard {...props} CardComponent={ModalBottomCard}>
      {props.children}
    </ModalCard>
  )
}
